import { AxiosError } from 'axios';

import type { CreateKotItemRequest, OrderItem } from '@billx/types';

import type {
  NucleusServiceErrorResponse,
  NucleusServiceResponse,
  NucleusServiceSuccessResponse,
} from './types';

export const mapToKotItemRequest = (item: OrderItem): CreateKotItemRequest => ({
  itemId: item.itemId,
  subtotal: item.price * item.quantity,
  quantity: item.quantity,
});

export const handleAxiosError = <T>(
  error: unknown,
  fallback?: string
): NucleusServiceResponse<T> => {
  if (error instanceof AxiosError && error.response?.data) {
    const errorData = error.response.data;
    // If the API already returns the correct structure, use it
    if (
      typeof errorData === 'object' &&
      errorData !== null &&
      'success' in errorData &&
      errorData.success === false
    ) {
      return errorData as NucleusServiceResponse<T>;
    }
    // Otherwise construct from the error response
    return {
      success: false,
      errorCode:
        (errorData as { errorCode?: string })?.errorCode || 'UNKNOWN_ERROR',
      message:
        (errorData as { message?: string })?.message ||
        fallback ||
        'An error occurred',
    };
  }

  return {
    success: false,
    errorCode: 'UNKNOWN_ERROR',
    message: fallback || 'An error occurred',
  };
};

export const handleNucleusResponse = <T>(
  response: NucleusServiceResponse<T>
): NucleusServiceSuccessResponse<T> => {
  if (!response.success) {
    throw new ErrorFromResponse(response);
  }
  return response;
};

export class ErrorFromResponse extends Error {
  constructor(response: NucleusServiceErrorResponse) {
    super(response.message);
    this.name = response.errorCode;
  }
}

export type ShowNotificationFn = (options: {
  message: string;
  severity?: 'success' | 'info' | 'warning' | 'error';
  duration?: number;
  playSound?: boolean;
}) => void;

/**
 * Common error utility to handle specific error codes.
 * Returns true if the error was handled, false otherwise.
 */
export const handleSpecificApiErrors = (
  response: NucleusServiceResponse<any>,
  errorCodes: string[],
  showNotification: ShowNotificationFn
): boolean => {
  if (
    !response.success &&
    response.errorCode &&
    errorCodes.includes(response.errorCode)
  ) {
    showNotification({
      message: response.message || 'An error occurred',
      severity: 'error',
    });
    return true;
  }
  return false;
};
